import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import { MessageCircle, FileText } from 'lucide-react';
import { ApiUrlContext } from "../App";

const BlogPage = () => {
  const { courseId } = useParams();
  const [posts, setPosts] = useState([]);
  const [course, setCourse] = useState(null);
  const [newPost, setNewPost] = useState({
    title: "",
    content: "",
    category: "question"
  });
  const [userRole, setUserRole] = useState("student");
  const [userName, setUserName] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [commentingPostId, setCommentingPostId] = useState(null);
  const [newComments, setNewComments] = useState({});
  const apiUrl = useContext(ApiUrlContext);

  // useMemo ile categories'i posts değiştiğinde güncelle
  const categories = useMemo(() => {
    const baseCategories = [
      { 
        id: "all", 
        name: "Tümü", 
        icon: <FileText size={16} />, 
        color: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        count: posts.length
      },
      { 
        id: "question", 
        name: "Sorular", 
        icon: "❔", 
        color: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
        count: posts.filter(p => p.category === "question").length
      },
      { 
        id: "discussion", 
        name: "Tartışmalar", 
        icon: <MessageCircle size={16} />, 
        color: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
        count: posts.filter(p => p.category === "discussion").length
      }
    ];

    // Eğer instructor veya admin ise duyuru kategorisini ekle
    if (userRole === "instructor" || userRole === "admin") {
      baseCategories.push({
        id: "announcement", 
        name: "Duyurular", 
        icon: "📢", 
        color: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
        count: posts.filter(p => p.category === "announcement").length
      });
    }

    return baseCategories;
  }, [posts, userRole]);

  // stabilize fetch functions so they can safely be used in useEffect dependencies
  const fetchUserInfo = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/user/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const userData = await response.json();
        setUserRole(userData.role || "student");
        setUserName(userData.name || "Kullanıcı");
      }
    } catch (error) {
      console.error("Kullanıcı bilgisi alınamadı:", error);
    }
  }, [apiUrl]);

  const fetchBlogData = useCallback(async () => {
    setLoading(true);
    try {
      const courseResponse = await fetch(`${apiUrl}/courses/${courseId}`);
      if (courseResponse.ok) {
        const courseData = await courseResponse.json();
        setCourse(courseData);
      }

      const postsResponse = await fetch(`${apiUrl}/blog/course/${courseId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      if (postsResponse.ok) {
        const postsData = await postsResponse.json();
        setPosts(postsData);
      }
    } catch (error) {
      console.error("Blog verileri yüklenirken hata:", error);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, courseId]);

  useEffect(() => {
    fetchBlogData();
    fetchUserInfo();
  }, [fetchBlogData, fetchUserInfo]);

  const handleCreatePost = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/blog/course/${courseId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...newPost,
          authorName: userName
        })
      });

      if (response.ok) {
        const post = await response.json();
        setPosts([post, ...posts]);
        setNewPost({
          title: "",
          content: "",
          category: "question"
        });
        setActiveTab(post.category);
      }
    } catch (error) {
      console.error("Gönderi oluşturma hatası:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async (postId, commentContent) => {
    if (!commentContent.trim()) return;

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/blog/posts/${postId}/comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ 
          content: commentContent,
          authorName: userName
        })
      });

      if (response.ok) {
        setNewComments(prev => ({ ...prev, [postId]: "" }));
        setCommentingPostId(null);
        fetchBlogData();
      }
    } catch (error) {
      console.error("Yorum ekleme hatası:", error);
    }
  };

  const handleCommentInputChange = (postId, value) => {
    setNewComments(prev => ({ ...prev, [postId]: value }));
  };

  const filteredPosts = useMemo(() => {
    if (activeTab === "all") return posts;
    return posts.filter(post => post.category === activeTab);
  }, [posts, activeTab]);

  const getCategoryInfo = (categoryId) => {
    return categories.find(c => c.id === categoryId) || categories[0];
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) {
      return `${diffMins} dakika önce`;
    } else if (diffHours < 24) {
      return `${diffHours} saat önce`;
    } else if (diffDays === 1) {
      return "Dün";
    } else if (diffDays < 7) {
      return `${diffDays} gün önce`;
    } else {
      return date.toLocaleDateString('tr-TR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    }
  };

  if (loading && posts.length === 0) {
    return (
      <div style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "200px",
        color: "#1e293b",
        fontSize: "16px"
      }}>
        Yükleniyor...
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: "1200px",
      margin: "0 auto",
      padding: "2rem 1rem",
      backgroundColor: "#f8fafc",
      minHeight: "100vh"
    }}>
      {/* Başlık Bölümü */}
      <div style={{
        marginBottom: "2rem",
        textAlign: "center"
      }}>
        <h1 style={{
          fontSize: "2rem",
          fontWeight: "700",
          color: "#1e293b",
          marginBottom: "0.5rem",
          fontFamily: "Inter, system-ui, sans-serif"
        }}>
          {course ? course.title : "Kurs Forumu"}
        </h1>
        <p style={{
          color: "#64748b",
          fontSize: "1rem",
          fontFamily: "Inter, system-ui, sans-serif"
        }}>
          Soru sorabilir, tartışmalara katılabilir ve duyuruları takip edebilirsiniz.
        </p>
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 2.5fr",
        gap: "1.5rem",
        alignItems: "start"
      }}>
        
        {/* Sol Sidebar - Yeni Gönderi Formu */}
        <div style={{
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          padding: "1.25rem",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
          border: "1px solid #e2e8f0",
          position: "sticky",
          top: "1rem"
        }}>
          <h3 style={{
            fontSize: "1.1rem",
            fontWeight: "600",
            color: "#1e293b",
            marginBottom: "1rem",
            fontFamily: "Inter, system-ui, sans-serif"
          }}>
            Yeni Gönderi
          </h3>
          <form onSubmit={handleCreatePost}>
            <div style={{ display: "grid", gap: "0.75rem" }}>
              <div>
                <label style={{
                  display: "block",
                  marginBottom: "0.25rem",
                  fontWeight: "500",
                  color: "#374151",
                  fontSize: "0.8rem",
                  fontFamily: "Inter, system-ui, sans-serif"
                }}>
                  Kategori
                </label>
                <select
                  value={newPost.category}
                  onChange={(e) => setNewPost({ ...newPost, category: e.target.value })}
                  style={{
                    width: "100%",
                    padding: "0.5rem 0.75rem",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    fontSize: "0.85rem",
                    backgroundColor: "#ffffff",
                    fontFamily: "Inter, system-ui, sans-serif",
                    transition: "all 0.2s ease"
                  }}
                >
                  <option value="question">❔ Soru</option>
                  <option value="discussion"><MessageCircle size={16} className="inline-icon" /> Tartışma</option>
                  {(userRole === "instructor" || userRole === "admin") && (
                    <option value="announcement">Duyuru</option>
                  )}
                </select>
              </div>
              
              <div>
                <label style={{
                  display: "block",
                  marginBottom: "0.25rem",
                  fontWeight: "500",
                  color: "#374151",
                  fontSize: "0.8rem",
                  fontFamily: "Inter, system-ui, sans-serif"
                }}>
                  Başlık
                </label>
                <input
                  type="text"
                  value={newPost.title}
                  onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                  placeholder="Gönderi başlığı..."
                  style={{
                    width: "100%",
                    padding: "0.5rem 0.75rem",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    fontSize: "0.85rem",
                    backgroundColor: "#ffffff",
                    fontFamily: "Inter, system-ui, sans-serif",
                    transition: "all 0.2s ease"
                  }}
                  required
                />
              </div>
              
              <div>
                <label style={{
                  display: "block",
                  marginBottom: "0.25rem",
                  fontWeight: "500",
                  color: "#374151",
                  fontSize: "0.8rem",
                  fontFamily: "Inter, system-ui, sans-serif"
                }}>
                  İçerik
                </label>
                <textarea
                  value={newPost.content}
                  onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                  placeholder="Sorunuzu veya görüşünüzü detaylı olarak yazın..."
                  rows="3"
                  style={{
                    width: "100%",
                    padding: "0.5rem 0.75rem",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    fontSize: "0.85rem",
                    backgroundColor: "#ffffff",
                    fontFamily: "Inter, system-ui, sans-serif",
                    resize: "vertical",
                    transition: "all 0.2s ease"
                  }}
                  required
                />
              </div>
              
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: "100%",
                  background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                  color: "#ffffff",
                  border: "none",
                  padding: "0.6rem 1rem",
                  borderRadius: "8px",
                  cursor: loading ? "not-allowed" : "pointer",
                  fontSize: "0.85rem",
                  fontWeight: "600",
                  fontFamily: "Inter, system-ui, sans-serif",
                  transition: "all 0.2s ease",
                  marginTop: "0.5rem"
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.target.style.background = "linear-gradient(135deg, #047857 0%, #065f46 100%)";
                    e.target.style.transform = "translateY(-1px)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.target.style.background = "linear-gradient(135deg, #059669 0%, #047857 100%)";
                    e.target.style.transform = "translateY(0)";
                  }
                }}
              >
                {loading ? "Gönderiliyor..." : "Gönderiyi Paylaş"}
              </button>
            </div>
          </form>
        </div>

        {/* Ana İçerik */}
        <div>
          {/* TEK SATIR KATEGORİ FİLTRELEME */}
          <div style={{
            display: "flex",
            gap: "0.75rem",
            marginBottom: "1.5rem",
            paddingBottom: "0.5rem",
            overflowX: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": {
              display: "none"
            }
          }}>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveTab(category.id)}
                style={{
                  flex: "0 0 auto",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  background: activeTab === category.id ? category.color : "#ffffff",
                  color: activeTab === category.id ? "#ffffff" : "#64748b",
                  padding: "0.6rem 1rem",
                  borderRadius: "10px",
                  cursor: "pointer",
                  border: "none",
                  boxShadow: activeTab === category.id 
                    ? "0 4px 12px rgba(0, 0, 0, 0.15)" 
                    : "0 1px 3px rgba(0, 0, 0, 0.1)",
                  transition: "all 0.2s ease",
                  fontFamily: "Inter, system-ui, sans-serif",
                  fontSize: "0.85rem",
                  fontWeight: "500",
                  whiteSpace: "nowrap"
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== category.id) {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 3px 8px rgba(0, 0, 0, 0.12)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== category.id) {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.1)";
                  }
                }}
              >
                <span style={{ fontSize: "1rem" }}>{category.icon}</span>
                <span>{category.name}</span>
                <span style={{
                  backgroundColor: activeTab === category.id ? "rgba(255,255,255,0.2)" : "rgba(100, 116, 139, 0.1)",
                  color: activeTab === category.id ? "#ffffff" : "#64748b",
                  padding: "0.1rem 0.4rem",
                  borderRadius: "12px",
                  fontSize: "0.75rem",
                  fontWeight: "600"
                }}>
                  {category.count}
                </span>
              </button>
            ))}
          </div>

          {/* Gönderiler */}
          <div style={{ display: "grid", gap: "1rem" }}>
            {filteredPosts.length === 0 ? (
              <div style={{
                backgroundColor: "#ffffff",
                borderRadius: "12px",
                padding: "2rem",
                textAlign: "center",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
                border: "1px solid #e2e8f0"
              }}>
                <div style={{
                  fontSize: "2rem",
                  marginBottom: "0.75rem",
                  opacity: 0.5
                }}>
                  <FileText size={20} className="inline-icon" />
                </div>
                <h3 style={{
                  fontSize: "1rem",
                  fontWeight: "600",
                  color: "#374151",
                  marginBottom: "0.25rem"
                }}>
                  {activeTab === "all" 
                    ? "Henüz hiç gönderi yok"
                    : `${categories.find(c => c.id === activeTab)?.name} kategorisinde gönderi yok`
                  }
                </h3>
                <p style={{
                  color: "#64748b",
                  fontSize: "0.85rem"
                }}>
                  {activeTab === "all" 
                    ? "İlk gönderiyi siz oluşturun!"
                    : "Bu kategoride ilk gönderiyi siz oluşturun!"
                  }
                </p>
              </div>
            ) : (
              filteredPosts.map((post) => (
                <PostCard
                  key={post._id}
                  post={post}
                  onAddComment={handleAddComment}
                  userRole={userRole}
                  userName={userName}
                  commentingPostId={commentingPostId}
                  setCommentingPostId={setCommentingPostId}
                  newComment={newComments[post._id] || ""}
                  onCommentInputChange={handleCommentInputChange}
                  formatDate={formatDate}
                  getCategoryInfo={getCategoryInfo}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

function PostCard({ 
  post, 
  onAddComment, 
  userRole, 
  userName, 
  commentingPostId, 
  setCommentingPostId, 
  newComment, 
  onCommentInputChange,
  formatDate,
  getCategoryInfo
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const categoryInfo = getCategoryInfo(post.category);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    onAddComment(post._id, newComment);
  };

  return (
    <div style={{
      backgroundColor: "#ffffff",
      borderRadius: "10px",
      padding: "1rem",
      boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
      border: "1px solid #e6eef6",
      transition: "all 0.2s ease"
    }}>
      {/* Gönderi Başlığı ve Kategori */}
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "flex-start",
        marginBottom: "0.75rem"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <div style={{
            background: categoryInfo.color,
            color: "#fff",
            padding: "0.2rem 0.6rem",
            borderRadius: "20px",
            fontSize: "0.7rem",
            fontWeight: "600",
            display: "flex",
            alignItems: "center",
            gap: "0.25rem"
          }}>
            <span>{categoryInfo.icon}</span>
            <span>{categoryInfo.name}</span>
          </div>
          <h4 style={{ 
            margin: 0, 
            fontSize: "1rem", 
            fontWeight: "600",
            color: "#1e293b"
          }}>
            {post.title}
          </h4>
        </div>
        <div style={{ fontSize: "0.75rem", color: "#64748b" }}>
          {formatDate(post.createdAt)}
        </div>
      </div>

      {/* Gönderi İçeriği */}
      <div style={{ 
        marginBottom: "0.75rem", 
        color: "#334155",
        lineHeight: "1.5",
        fontSize: "0.9rem"
      }}>
        <p style={{ 
          margin: 0,
          display: "-webkit-box",
          WebkitLineClamp: isExpanded ? "none" : 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden"
        }}>
          {post.content}
        </p>
        {post.content.length > 200 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            style={{
              background: "none",
              border: "none",
              color: "#3b82f6",
              fontSize: "0.8rem",
              cursor: "pointer",
              padding: "0.25rem 0",
              marginTop: "0.25rem"
            }}
          >
            {isExpanded ? "Daha az göster" : "Devamını oku..."}
          </button>
        )}
      </div>

      {/* Gönderi Bilgileri */}
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center",
        marginBottom: "0.75rem",
        paddingBottom: "0.75rem",
        borderBottom: "1px dashed #e2e8f0"
      }}>
        <div style={{ fontSize: "0.8rem", color: "#64748b" }}>
          <span style={{ fontWeight: "500" }}>{post.authorName || post.authorEmail || "Anonim"}</span>
          <span style={{ margin: "0 0.25rem" }}>•</span>
          <span>{post.comments?.length || 0} yorum</span>
        </div>
        
        <button
          onClick={() => setCommentingPostId(commentingPostId === post._id ? null : post._id)}
          style={{
            background: "none",
            border: "none",
            color: "#3b82f6",
            fontSize: "0.8rem",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "0.25rem",
            padding: "0.25rem 0.5rem",
            borderRadius: "4px",
            transition: "all 0.2s ease"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#eff6ff";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          <span><MessageCircle size={16} className="inline-icon" /></span>
          <span>Yorum Yap</span>
        </button>
      </div>

      {/* Yorumlar */}
      {post.comments && post.comments.length > 0 && (
        <div style={{ 
          marginBottom: "0.75rem",
          maxHeight: isExpanded ? "none" : "200px",
          overflow: "hidden",
          transition: "all 0.3s ease"
        }}>
          {post.comments.slice(0, isExpanded ? post.comments.length : 2).map((comment) => (
            <div key={comment._id} style={{ 
              marginBottom: "0.5rem",
              padding: "0.5rem",
              backgroundColor: "#f8fafc",
              borderRadius: "8px"
            }}>
              <div style={{ 
                display: "flex", 
                justifyContent: "space-between", 
                alignItems: "flex-start",
                marginBottom: "0.25rem"
              }}>
                <div style={{ 
                  fontSize: "0.8rem", 
                  fontWeight: 600,
                  color: "#1e293b"
                }}>
                  {comment.authorName || comment.authorEmail || "Anonim"}
                </div>
                <div style={{ 
                  fontSize: "0.7rem", 
                  color: "#94a3b8" 
                }}>
                  {formatDate(comment.createdAt)}
                </div>
              </div>
              <div style={{ 
                fontSize: "0.85rem", 
                color: "#475569",
                lineHeight: "1.4"
              }}>
                {comment.content}
              </div>
            </div>
          ))}
          
          {post.comments.length > 2 && !isExpanded && (
            <button
              onClick={() => setIsExpanded(true)}
              style={{
                background: "none",
                border: "none",
                color: "#3b82f6",
                fontSize: "0.8rem",
                cursor: "pointer",
                padding: "0.25rem 0",
                marginTop: "0.25rem"
              }}
            >
              + {post.comments.length - 2} yorum daha göster
            </button>
          )}
        </div>
      )}

      {/* Yorum Formu */}
      {commentingPostId === post._id && (
        <form onSubmit={handleSubmit} style={{ 
          marginTop: "0.75rem",
          paddingTop: "0.75rem",
          borderTop: "1px solid #e2e8f0"
        }}>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <input
              value={newComment}
              onChange={(e) => onCommentInputChange(post._id, e.target.value)}
              placeholder="Yorumunuzu yazın..."
              style={{
                flex: 1,
                padding: "0.5rem 0.75rem",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
                fontSize: "0.85rem",
                transition: "all 0.2s ease"
              }}
              autoFocus
            />
            <button 
              type="submit"
              style={{
                background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                color: "#fff",
                border: "none",
                padding: "0.5rem 1rem",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "0.85rem",
                fontWeight: "500",
                transition: "all 0.2s ease"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              Gönder
            </button>
            <button 
              type="button"
              onClick={() => setCommentingPostId(null)}
              style={{
                background: "none",
                color: "#64748b",
                border: "1px solid #e2e8f0",
                padding: "0.5rem 0.75rem",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "0.85rem",
                transition: "all 0.2s ease"
              }}
            >
              İptal
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default BlogPage;
